
import React, { useRef, useCallback, memo } from 'react';
import { ScrollArea } from "@/components/ui/scroll-area";
import { LayoutDashboard, Shield, LogOut } from "lucide-react";
import NavItem from './NavItem';
import SidebarLogo from './SidebarLogo';
import SidebarSections from './SidebarSections';
import { ExpandedGroups } from './types';
import { useAuth } from '@/hooks/useAuth.tsx';
import { Button } from '@/components/ui/button';

interface SidebarContentProps {
  expandedGroups: ExpandedGroups;
  toggleGroup: (group: keyof ExpandedGroups) => void;
  isActive: (path: string) => boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  isCompact?: boolean;
}

/**
 * SidebarContent - Main sidebar navigation component
 * Optimized with memoization, useCallback, and improved error handling
 */
const SidebarContent: React.FC<SidebarContentProps> = memo(({ 
  expandedGroups, 
  toggleGroup, 
  isActive, 
  scrollContainerRef,
  isCompact = false
}) => {
  const localScrollRef = useRef<HTMLDivElement>(null);
  const actualScrollRef = scrollContainerRef || localScrollRef;
  const { isAdmin, signOut } = useAuth();
  
  // Memoize the logout handler to prevent recreating on every render
  const handleLogout = useCallback(async () => {
    try {
      await signOut();
      window.location.href = '/signin';
    } catch (error) {
      console.error('Logout error:', error);
      // Still try to redirect even if signOut fails
      window.location.href = '/signin?error=logout';
    }
  }, [signOut]);
  
  return (
    <div className="flex flex-col h-full">
      <SidebarLogo isCompact={isCompact} />
      <div className="h-px w-full bg-border/40 mb-0.5"></div>
      <ScrollArea 
        className="flex-1 px-2 py-0 -mt-2" 
        scrollHideDelay={100}
      >
        <div className="space-y-0.5">
          <NavItem 
            to="/dashboard"
            icon={LayoutDashboard}
            isActive={isActive('/dashboard')}
            isCompact={isCompact}
          >
            Dashboard
          </NavItem>
          {isAdmin && (
            <NavItem 
              to="/admin-dashboard"
              icon={Shield}
              isActive={isActive('/admin-dashboard')}
              isCompact={isCompact}
            >
              Admin
            </NavItem>
          )}
        </div>

        <div className="h-px w-full bg-border/40 my-0.5" />

        <SidebarSections 
          expandedGroups={expandedGroups}
          toggleGroup={toggleGroup}
          isActive={isActive}
          isCompact={isCompact}
        />
      </ScrollArea>
      
      {/* Logout button at the bottom of sidebar */}
      <div className="px-2 py-2 border-t border-border/40">
        <Button 
          variant="ghost"
          size="sm"
          className="w-full justify-start text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 font-medium"
          onClick={handleLogout}
        >
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );
});

// Add display name for better debugging
SidebarContent.displayName = 'SidebarContent';

export default SidebarContent;
